/**
 * Session Recording Controls
 * Record jam sessions with multi-track capture and cloud sync
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Circle,
  Square,
  Pause,
  Play,
  Download,
  Cloud,
  CloudOff,
  Trash2,
  Clock,
  Mic,
  Music2,
  ChevronDown,
  ChevronUp,
  Check,
  Loader2,
  FolderOutput,
  Layers,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

// IndexedDB for local storage
const DB_NAME = 'string-sauce-recordings';
const DB_VERSION = 1;
const STORE_NAME = 'clips';

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
};

const saveClipLocally = async (clipId, audioBlob, metadata) => {
  const db = await openDB();
  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const store = transaction.objectStore(STORE_NAME);
  
  return new Promise((resolve, reject) => {
    const request = store.put({
      id: clipId,
      audioBlob,
      metadata,
      timestamp: Date.now()
    });
    request.onsuccess = () => resolve(clipId);
    request.onerror = () => reject(request.error);
  });
};

const getClipLocally = async (clipId) => {
  const db = await openDB();
  const transaction = db.transaction([STORE_NAME], 'readonly');
  const store = transaction.objectStore(STORE_NAME);
  
  return new Promise((resolve, reject) => {
    const request = store.get(clipId);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const deleteClipLocally = async (clipId) => {
  const db = await openDB();
  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const store = transaction.objectStore(STORE_NAME);
  
  return new Promise((resolve, reject) => {
    const request = store.delete(clipId);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Format duration as MM:SS
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Clip item component
const ClipItem = ({ clip, isOwn, isHost, onDelete, onSync, isSyncing }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -10 }}
    className="flex items-center gap-3 p-2 rounded-lg bg-zinc-800/50 border border-zinc-700"
  >
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium"
      style={{ backgroundColor: clip.color }}
    >
      {clip.user_name?.charAt(0)?.toUpperCase()}
    </div>
    
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-sm text-white truncate">{clip.user_name}</span>
        <span className="text-xs text-zinc-500">Take {clip.take_number}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <Clock className="w-3 h-3" />
        {formatDuration(clip.duration)}
        {clip.audio_url ? (
          <span className="flex items-center gap-1 text-emerald-400">
            <Cloud className="w-3 h-3" />
            Synced
          </span>
        ) : (
          <span className="flex items-center gap-1 text-amber-400">
            <CloudOff className="w-3 h-3" />
            Local
          </span>
        )}
      </div>
    </div>
    
    <div className="flex items-center gap-1">
      {!clip.audio_url && (isOwn || isHost) && (
        <button
          onClick={() => onSync?.(clip.id)}
          disabled={isSyncing}
          className="p-1.5 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"
          title="Sync to cloud"
        >
          {isSyncing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Cloud className="w-4 h-4" />
          )}
        </button>
      )}
      {(isOwn || isHost) && (
        <button
          onClick={() => onDelete?.(clip.id)}
          className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          title="Delete clip"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  </motion.div>
);

export const SessionRecording = ({
  session,
  userId,
  onSend,
  onExport,
  mediaRecorder,
  audioStream,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [syncingClips, setSyncingClips] = useState({});
  const [localRecording, setLocalRecording] = useState(false);
  
  const durationIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingStartTimeRef = useRef(null);

  const recording = session?.recording || {};
  const clips = recording.clips || [];
  const isHost = session?.participants?.find(p => p.user_id === userId)?.role === 'host';
  const isRecording = recording.status === 'recording';
  const isPaused = recording.status === 'paused';

  // Duration timer
  useEffect(() => {
    if (isRecording && !isPaused) {
      durationIntervalRef.current = setInterval(() => {
        if (recording.started_at) {
          const start = new Date(recording.started_at);
          const now = new Date();
          setRecordingDuration((now - start) / 1000);
        }
      }, 100);
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [isRecording, isPaused, recording.started_at]);

  // Start local recording when session recording starts
  useEffect(() => {
    if (isRecording && !isPaused && audioStream && !localRecording) {
      startLocalRecording();
    } else if ((!isRecording || isPaused) && localRecording) {
      stopLocalRecording();
    }
  }, [isRecording, isPaused, audioStream, localRecording]);

  const startLocalRecording = async () => {
    if (!audioStream) {
      toast.error('No audio stream available');
      return;
    }

    try {
      const mediaRecorder = new MediaRecorder(audioStream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      chunksRef.current = [];
      recordingStartTimeRef.current = Date.now();

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const duration = (Date.now() - recordingStartTimeRef.current) / 1000;
        
        // Generate clip ID
        const clipId = `clip_${Date.now()}_${userId}`;
        
        // Save locally first
        await saveClipLocally(clipId, audioBlob, {
          userId,
          sessionId: session?.id,
          duration,
          takeNumber: recording.take_count || 1
        });

        // Notify server
        onSend?.({
          type: 'add_clip',
          local_key: clipId,
          start_time: recording.started_at ? 0 : recordingStartTimeRef.current / 1000,
          duration,
        });

        toast.success('Clip recorded and saved locally');
      };

      mediaRecorder.start(1000); // Capture in 1-second chunks
      mediaRecorderRef.current = mediaRecorder;
      setLocalRecording(true);
    } catch (err) {
      console.error('Error starting local recording:', err);
      toast.error('Failed to start recording');
    }
  };

  const stopLocalRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setLocalRecording(false);
  };

  // Host controls
  const handleStartRecording = () => {
    onSend?.({ type: 'start_recording' });
  };

  const handleStopRecording = () => {
    onSend?.({ type: 'stop_recording' });
  };

  const handlePauseRecording = () => {
    onSend?.({ type: 'pause_recording' });
  };

  const handleResumeRecording = () => {
    onSend?.({ type: 'resume_recording' });
  };

  // Clip management
  const handleDeleteClip = async (clipId) => {
    if (window.confirm('Delete this clip?')) {
      try {
        await deleteClipLocally(clipId);
        onSend?.({ type: 'delete_clip', clip_id: clipId });
      } catch (err) {
        console.error('Error deleting clip:', err);
        toast.error('Failed to delete clip');
      }
    }
  };

  const handleSyncClip = async (clipId) => {
    setSyncingClips(prev => ({ ...prev, [clipId]: true }));
    
    try {
      // Get clip from local storage
      const localClip = await getClipLocally(clipId);
      if (!localClip) {
        throw new Error('Clip not found locally');
      }

      // TODO: Upload to cloud storage
      // For now, we'll simulate the upload
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In production, upload to your cloud storage and get URL
      const fakeUrl = `https://storage.example.com/clips/${clipId}.webm`;
      
      onSend?.({
        type: 'sync_clip_to_cloud',
        clip_id: clipId,
        audio_url: fakeUrl
      });
      
      toast.success('Clip synced to cloud');
    } catch (err) {
      console.error('Error syncing clip:', err);
      toast.error('Failed to sync clip');
    } finally {
      setSyncingClips(prev => ({ ...prev, [clipId]: false }));
    }
  };

  const handleExportSession = () => {
    onSend?.({ type: 'export_session', format: 'project' });
    onExport?.(recording);
  };

  // Group clips by user
  const clipsByUser = clips.reduce((acc, clip) => {
    if (!acc[clip.user_id]) {
      acc[clip.user_id] = [];
    }
    acc[clip.user_id].push(clip);
    return acc;
  }, {});

  return (
    <div className="border-t border-zinc-800" data-testid="session-recording">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            isRecording ? 'bg-red-500 animate-pulse' : 
            isPaused ? 'bg-amber-500' : 
            'bg-zinc-500'
          }`} />
          <span className="text-sm font-medium text-white">Session Recording</span>
          {isRecording && (
            <span className="text-xs text-red-400 font-mono">
              {formatDuration(recordingDuration)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">{clips.length} clips</span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-zinc-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          )}
        </div>
      </button>

      {/* Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-4">
              {/* Recording Controls (Host Only) */}
              {isHost && (
                <div className="flex items-center gap-2">
                  {!isRecording && recording.status !== 'paused' ? (
                    <Button
                      onClick={handleStartRecording}
                      className="flex-1 bg-red-500 hover:bg-red-600"
                      data-testid="start-session-recording"
                    >
                      <Circle className="w-4 h-4 mr-2 fill-current" />
                      Start Recording
                    </Button>
                  ) : (
                    <>
                      {isPaused ? (
                        <Button
                          onClick={handleResumeRecording}
                          className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Resume
                        </Button>
                      ) : (
                        <Button
                          onClick={handlePauseRecording}
                          variant="outline"
                          className="flex-1 border-amber-500 text-amber-400 hover:bg-amber-500/10"
                        >
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </Button>
                      )}
                      <Button
                        onClick={handleStopRecording}
                        variant="destructive"
                        className="flex-1"
                        data-testid="stop-session-recording"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Stop
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Recording Status for Non-Hosts */}
              {!isHost && isRecording && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <Circle className="w-4 h-4 text-red-500 fill-current animate-pulse" />
                  <span className="text-sm text-red-400">Recording in progress...</span>
                  <span className="text-xs text-zinc-400 font-mono ml-auto">
                    {formatDuration(recordingDuration)}
                  </span>
                </div>
              )}

              {/* Take Counter */}
              {recording.take_count > 0 && (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Layers className="w-3 h-3" />
                  Take {recording.take_count}
                  {recording.status === 'stopped' && recording.duration > 0 && (
                    <span className="ml-auto">
                      Total: {formatDuration(recording.duration)}
                    </span>
                  )}
                </div>
              )}

              {/* Clips List */}
              {clips.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  <AnimatePresence>
                    {clips.map((clip) => (
                      <ClipItem
                        key={clip.id}
                        clip={clip}
                        isOwn={clip.user_id === userId}
                        isHost={isHost}
                        onDelete={handleDeleteClip}
                        onSync={handleSyncClip}
                        isSyncing={syncingClips[clip.id]}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="text-center py-6 text-zinc-500">
                  <Music2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No clips recorded yet</p>
                  <p className="text-xs mt-1">
                    {isHost ? 'Press Start Recording to begin' : 'Waiting for host to start recording'}
                  </p>
                </div>
              )}

              {/* Export Button */}
              {clips.length > 0 && isHost && recording.status === 'stopped' && (
                <Button
                  onClick={handleExportSession}
                  variant="outline"
                  className="w-full border-orange-500 text-orange-400 hover:bg-orange-500/10"
                  data-testid="export-session"
                >
                  <FolderOutput className="w-4 h-4 mr-2" />
                  Export to Project
                </Button>
              )}

              {/* Info */}
              <div className="flex items-start gap-2 p-2 bg-zinc-800/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-zinc-500">
                  Clips are saved locally first for instant playback. 
                  Sync to cloud for permanent storage and sharing.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SessionRecording;
